import { useState, useCallback } from 'react';
import { TickSnapshot } from '@/types/tickSnapshot';
import { getFilteredTickSnapshots } from '@/lib/api/tickSnapshotApi';

// The filtered endpoint returns the same stock twice when it's listed on
// both NSE and BSE — same `id`, same data, different `exchange`. Duplicate
// ids collide on the table's React keys and break sorting. Collapse to one
// row per id, preferring the NSE listing.
function dedupeById(snapshots: TickSnapshot[]): TickSnapshot[] {
  const byId = new Map<number, TickSnapshot>();
  for (const snap of snapshots) {
    const existing = byId.get(snap.id);
    if (!existing) {
      byId.set(snap.id, snap);
      continue;
    }
    if (existing.exchange !== 'NSE' && snap.exchange === 'NSE') {
      byId.set(snap.id, snap);
    }
  }
  return Array.from(byId.values());
}

export function useFilteredTickSnapshots() {
  const [snapshots, setSnapshots] = useState<TickSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFilteredTickSnapshots = useCallback(async (filterType: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await getFilteredTickSnapshots(filterType);
      const deduped = dedupeById(data);
      setSnapshots(deduped);
      return deduped;
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Failed to load filtered tick snapshots. Please try again later.';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    snapshots,
    loading,
    error,
    fetchFilteredTickSnapshots,
  };
}
